import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, GripVertical, BarChart3, Layers, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useMarket } from "../lib/store";
import { useSubscribe, useTick, useMeta } from "../lib/socket";
import { useWatchlists } from "../lib/watchlists";
import { pct, signed, tone } from "../lib/format";
import { Price, ExTag } from "./Price";
import { Sparkline } from "./motion";
import { useFlashThrottle } from "./motion/hooks";
import { MarketDepth } from "./MarketDepth";

function WatchRow({ k, idx, onDragStart, onDrop }) {
  const t = useTick(k);
  const inst = useMeta(k);
  const nav = useNavigate();
  const { openOrder, setFocus, depthKey, setDepth } = useMarket();
  const remove = useWatchlists((s) => s.remove);
  const sym = inst?.trading_symbol || k.split("|")[1];
  const showDepth = depthKey === k;
  const [flash, setFlash] = useState("");
  const allowFlash = useFlashThrottle(4);
  useEffect(() => {
    if (t?.prevLtp == null || t?.ltp == null || t.prevLtp === t.ltp || !allowFlash()) return undefined;
    setFlash(t.ltp > t.prevLtp ? "row-flash-up" : "row-flash-down");
    const id = setTimeout(() => setFlash(""), 800);
    return () => clearTimeout(id);
  }, [t?.ltp, t?.prevLtp]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = (e, fn) => { e.stopPropagation(); fn(); };
  return (
    <div
      draggable onDragStart={() => onDragStart(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
      onMouseEnter={() => setFocus(k)} data-testid={`watchlist-item-${sym}`}
      className="border-b border-border/60"
    >
      <div className={`group relative flex items-center h-10 px-3 hover:bg-accent/60 transition-colors duration-150 cursor-pointer ${flash} ${showDepth ? "bg-accent/40 shadow-[inset_2px_0_0_hsl(var(--primary))]" : ""}`}
        onClick={() => setDepth(showDepth ? null : k)}>
        <GripVertical className="h-3 w-3 text-muted-foreground/40 -ml-2 mr-1 opacity-0 group-hover:opacity-100 cursor-grab" />
        <div className="flex-1 min-w-0">
          <div className={`text-[12.5px] font-medium truncate ${t ? tone(t.ch) : ""}`}>{sym}</div>
          <div className="flex items-center gap-1 mt-0.5"><ExTag ex={inst?.exchange} />
            {inst?.expiry && <span className="text-[9px] text-muted-foreground">{inst.expiry}</span>}
          </div>
        </div>
        <div className="text-right group-hover:opacity-0 transition-opacity duration-100">
          <div className="flex items-center justify-end gap-2"><Sparkline k={k} /><div className={`text-[12.5px] ${tone(t?.ch)}`}><Price value={t?.ltp} prev={t?.prevLtp} arrow /></div></div>
          <div className="text-[10.5px] num text-muted-foreground">{signed(t?.ch)} <span className={tone(t?.ch)}>{pct(t?.chp)}</span></div>
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1">
          <button data-testid={`watchlist-buy-${sym}`} onClick={(e) => act(e, () => openOrder({ key: k, side: "BUY" }))}
            className="h-6 w-7 rounded bg-up text-white text-[11px] font-bold hover:brightness-110 shadow-[0_0_10px_hsl(var(--up)/0.35)] active:scale-95 transition-transform duration-100">B</button>
          <button data-testid={`watchlist-sell-${sym}`} onClick={(e) => act(e, () => openOrder({ key: k, side: "SELL" }))}
            className="h-6 w-7 rounded bg-down text-white text-[11px] font-bold hover:brightness-110 shadow-[0_0_10px_hsl(var(--down)/0.35)] active:scale-95 transition-transform duration-100">S</button>
          <button title="Market depth" data-testid={`watchlist-depth-${sym}`} onClick={(e) => act(e, () => setDepth(showDepth ? null : k))}
            className="h-6 w-7 rounded border border-border bg-card grid place-items-center hover:bg-accent"><Layers className="h-3 w-3" /></button>
          <button title="Chart" data-testid={`watchlist-chart-${sym}`} onClick={(e) => act(e, () => nav(`/instrument/${encodeURIComponent(k)}`))}
            className="h-6 w-7 rounded border border-border bg-card grid place-items-center hover:bg-accent"><BarChart3 className="h-3 w-3" /></button>
          <button title="Remove" data-testid={`watchlist-remove-${sym}`} onClick={(e) => act(e, () => remove(k))}
            className="h-6 w-7 rounded border border-border bg-card grid place-items-center hover:bg-accent"><Trash2 className="h-3 w-3" /></button>
        </div>
      </div>
      {showDepth && <div className="px-3 pb-3 pt-1 bg-muted/30 rise"><MarketDepth instrumentKey={k} /></div>}
    </div>
  );
}

function InlineSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const add = useWatchlists((s) => s.add);
  const addMeta = useMarket((s) => s.addMeta);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return undefined; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/instruments/search", { params: { q } });
      setRes(data);
      addMeta(Object.fromEntries(data.map((d) => [d.instrument_key, d])));
    }, 200);
    return () => clearTimeout(t);
  }, [q, addMeta]);
  return (
    <div className="relative px-3 py-2 border-b border-border">
      <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
      <input data-testid="watchlist-search-input" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search & add, e.g. INFY, NIFTY 25000 CE"
        className="w-full h-8 pl-7 pr-7 rounded-md bg-muted/50 border border-transparent focus:border-primary/60 outline-none text-[12px] placeholder:text-muted-foreground/70" />
      {q && <button onClick={() => setQ("")} className="absolute right-5 top-1/2 -translate-y-1/2"><X className="h-3.5 w-3.5 text-muted-foreground" /></button>}
      {res.length > 0 && (
        <div className="absolute left-3 right-3 top-full mt-1 z-30 max-h-80 overflow-auto thin-scroll rounded-md border border-border bg-popover shadow-2xl">
          {res.map((r) => (
            <button key={r.instrument_key} data-testid={`watchlist-search-result-${r.trading_symbol}`}
              onClick={() => { add(r.instrument_key); setQ(""); }}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-accent text-[12px]">
              <span className="truncate"><span className="font-medium">{r.trading_symbol}</span>
                <span className="text-muted-foreground ml-2 text-[10.5px]">{r.name}</span></span>
              <ExTag ex={r.exchange} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Watchlist() {
  const { lists, active, setActive, load, save } = useWatchlists();
  const drag = useRef(null);
  useEffect(() => { load(); }, [load]);
  const cur = lists.find((l) => l.index === active);
  const items = cur?.items || [];
  useSubscribe(items);
  const onDrop = (to) => {
    const from = drag.current;
    if (from == null || from === to) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    save(active, next);
  };
  return (
    <div className="flex flex-col h-full" data-testid="watchlist-panel">
      <InlineSearch />
      <div className="flex items-center justify-between px-3 py-1.5 text-[10.5px] text-muted-foreground border-b border-border">
        <span className="font-medium">{cur?.name || "Watchlist"}</span>
        <span className="num" data-testid="watchlist-count">{items.length}/50</span>
      </div>
      <div className="flex-1 overflow-auto thin-scroll">
        {items.length === 0 && (
          <div className="p-6 text-center text-muted-foreground text-[12px]" data-testid="watchlist-empty">
            This watchlist is empty. Search above to add instruments.
          </div>
        )}
        {items.map((k, i) => <WatchRow key={k} k={k} idx={i} onDragStart={(x) => { drag.current = x; }} onDrop={onDrop} />)}
      </div>
      <div className="flex border-t border-border">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <button key={i} data-testid={`watchlist-tab-${i}`} onClick={() => setActive(i)}
            className={`relative flex-1 h-9 text-[12px] num transition-colors duration-150 ${active === i ? "text-primary bg-primary/[0.07] font-semibold" : "text-muted-foreground hover:bg-accent"}`}>
            {active === i && <span className="absolute inset-x-2 top-0 h-[2px] rounded-b bg-gold" />}
            {i}
          </button>
        ))}
      </div>
    </div>
  );
}
